// THE PRESS'S STATE MACHINE — Pay, Move to Treasury and Refill Hot (UI rebuild batch 4, custody hand-off).
//
// COPIED, NOT RE-DERIVED: this is screens/custody/MoveSheet.tsx's wiring, line for line, lifted out of its JSX so the
// three focused pages can draw it in Astra's form anatomy. One immutable attempt under one key (db/0085), locked
// through sending and recovery, kept across a route change (the store in lib/custody-screen.ts), asked after by key
// every five seconds while unresolved, voided before a changed payment (db/0093); the caps, the whitelist test, the
// Flagged one-release rule and the window judged exactly as the sheet judges them (newPaymentBlocked, moveAllowance,
// newPaymentReadiness, flaggedReleaseOpen). The only difference: the page is "open" for as long as it is mounted.
import { useEffect, useReducer, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { custody as api, type PayoutSource } from '../../../lib/api';
import { amountProblem } from '../../../lib/custody-format';
import {
  moveSheetReducer, moveSheetIdle, fieldsLocked, sending, replayable, refusedAttempt, nextPayload, newPaymentBlocked, moveAllowance,
  flaggedReleaseOpen, saveUnresolved, loadUnresolved, clearUnresolved, storeStep, trackedKey, readStateOf, newPaymentReadiness, newlyRecorded,
  heldForPick, type MoveAttempt,
} from '../../../lib/custody-screen';
import { useHeldChoice } from '../../../lib/useHeldChoice';

export interface MoveDestination {
  id: number;
  /** The destination as it is said: the customer's name, or the desk's own wallet's word. */
  name: string;
  address: string;
  /** The whitelist's stage (db/0098): 'approved' — only the test can be paid, and the amount is fixed to it. */
  stage?: 'not_approved' | 'approved' | 'test_sent' | 'whitelisted' | null;
  testPaymentUsdt?: string;
}

/** A press's key: a UUID where the browser has one, else time and chance (MoveSheet.tsx newRequestId). */
function newRequestId(): string {
  const c = globalThis.crypto as Crypto | undefined;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

export function useMoveAttempt({ destination, sources, onRecorded, deskReturn = false, withDeal = false }: {
  destination: MoveDestination | null;
  /** FLAGGED BACK TO HOT, the desk's own money (the wallet's item 5, db/0109, 3 Nov 2026): no depositor is named — the server
   *  refuses one (held_for_desk_money) — and the figure it may take is the desk's (`desk.held` on GET /flagged-held). */
  deskReturn?: boolean;
  /** A PAYOUT MAY NAME THE DEAL IT SETTLES (the wallet's P-063, b8241b2b): optional, sent as dealId. */
  withDeal?: boolean;
  /** Where the money may leave from (lib/custody-screen.ts paySources); the first is the default. */
  sources: readonly PayoutSource[];
  onRecorded?: (toWalletId: number) => void;
}) {
  const open = destination !== null;
  const qc = useQueryClient();
  const [amount, setAmount] = useState('');
  const [deal, setDeal] = useState<string | null>(null);
  // The deal belongs to ITS press: kept by the press's key, so a replay of an uncertain press sends the same deal (the sheet's
  // own snapshot carries only the payment's fields) and a changed deal is a new press (edit() gives it a new key).
  const dealFor = useRef(new Map<string, string | null>());
  const [from, setFrom] = useState<PayoutSource>(sources[0] ?? 'running_hot');
  const [sheet, dispatch] = useReducer(moveSheetReducer, undefined, () => moveSheetIdle(newRequestId()));
  const destinationId = destination?.id ?? null;
  const win = useQuery({ queryKey: ['custody', 'approval-window'], queryFn: api.approvalWindow, enabled: open });
  const limits = useQuery({ queryKey: ['custody', 'limits'], queryFn: api.limits, enabled: open, staleTime: 30_000, retry: false });
  const wantsFlagged = open && from === 'frozen';
  const approvals = useQuery({ queryKey: ['custody', 'approvals'], queryFn: api.approvals, enabled: wantsFlagged, staleTime: 10_000, retry: false });
  const balances = useQuery({ queryKey: ['custody', 'balances'], queryFn: api.balances, enabled: wantsFlagged, staleTime: 30_000, retry: false });
  // Out of Flagged, whose held money it is (db/0103 held_for): the one depositor pre-selected, his choice among several.
  const holders = useQuery({ queryKey: ['custody', 'flagged-held'], queryFn: api.flaggedHeld, enabled: wantsFlagged, staleTime: 10_000, retry: false });
  const [heldChoice, setHeldChoice, resetHeldChoice] = useHeldChoice(holders.data?.holders);
  const m = useMutation({
    mutationFn: (p: MoveAttempt) => {
      const dealId = dealFor.current.get(p.requestId) ?? null;
      return api.proposeMove({ toWalletId: p.toWalletId, amount: p.amount, from: p.from, requestId: p.requestId, ...(p.heldFor ? { heldFor: p.heldFor } : {}), ...(dealId ? { dealId } : {}) });
    },
    onMutate: (p) => dispatch({ type: 'submit', attempt: p }),
    onSuccess: (r, p) => {
      dispatch({ type: 'done', seq: p.seq, status: r.status ?? null, newId: newRequestId() });
      void qc.invalidateQueries({ queryKey: ['custody', 'approvals'] });
      void qc.invalidateQueries({ queryKey: ['custody', 'summary'] });
    },
    onError: (error, p) => dispatch({ type: 'failed', seq: p.seq, error, newId: newRequestId() }),
    onSettled: () => { void qc.invalidateQueries({ queryKey: ['custody', 'whitelist'] }); },
  });
  const toldKey = useRef<string | null>(null);
  useEffect(() => {
    const to = newlyRecorded(sheet.attempt, toldKey.current);
    if (to === null) return;
    toldKey.current = sheet.attempt!.requestId;
    void qc.invalidateQueries({ queryKey: ['custody', 'whitelist'] });
    onRecorded?.(to);
  }, [sheet.attempt]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open || destinationId === null) return;
    setFrom(sources[0] ?? 'running_hot');
    resetHeldChoice();
    dispatch({ type: 'open', destinationId, kept: loadUnresolved(destinationId), newId: newRequestId() });
  }, [open, destinationId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (sheet.attempt && (sheet.attempt.state === 'pending' || sheet.attempt.state === 'uncertain')) { setAmount(sheet.attempt.amount); setFrom(sheet.attempt.from); }
  }, [sheet.attempt]);
  useEffect(() => {
    const step = storeStep(sheet);
    if (!step) return;
    for (const c of step.clear) clearUnresolved(c.destinationId, c.requestId);
    if (step.save) saveUnresolved(step.save.toWalletId, step.save);
    if (step.clear.length > 0) dispatch({ type: 'acked', requestIds: step.clear.map((c) => c.requestId) });
  }, [sheet]);
  const key = trackedKey(sheet);
  const byKey = useQuery({
    queryKey: ['custody', 'move-by-request', key], queryFn: () => api.moveByRequest(key!),
    enabled: open && key !== null, refetchInterval: 5_000, retry: false, staleTime: 0,
  });
  useEffect(() => {
    if (key !== null && byKey.data?.recorded) dispatch({ type: 'recorded', requestId: key, status: byKey.data.status, newId: newRequestId() });
  }, [key, byKey.data]);
  const voiding = useMutation({
    mutationFn: (requestId: string) => api.voidPress(requestId),
    onSuccess: (r, requestId) => {
      if (r.outcome === 'recorded') { dispatch({ type: 'recorded', requestId, status: r.status, newId: newRequestId() }); void qc.invalidateQueries({ queryKey: ['custody', 'approvals'] }); }
      else { dispatch({ type: 'voided', requestId, newId: newRequestId() }); m.reset(); }
    },
  });

  const attempt = sheet.attempt;
  const locked = fieldsLocked(sheet);
  const testOnly = destination?.stage === 'approved' && (locked && attempt ? attempt.from : from) !== 'treasury';
  const fixedAmount = testOnly ? (limits.data?.testPaymentUsdt ?? destination?.testPaymentUsdt ?? '10') : null;
  const shownAmount = locked && attempt ? attempt.amount : (fixedAmount ?? amount);
  const shownFrom = locked && attempt ? attempt.from : from;
  const problem = shownAmount.trim() === '' ? null : amountProblem(shownAmount);
  const windowOpen = !!win.data?.window;
  const readiness = newPaymentReadiness(shownFrom, { limits: readStateOf(limits), approvals: readStateOf(approvals), balances: readStateOf(balances) });
  const allowance = limits.data && destination ? moveAllowance({ amount: shownAmount, from: shownFrom, limits: limits.data, stage: destination.stage ?? null }) : { ok: true as const };
  const releaseOpen = shownFrom === 'frozen' ? flaggedReleaseOpen(approvals.data?.approvals, balances.data?.wallets) : false;
  const edit = (next: () => void) => { if (locked) return; next(); dispatch({ type: 'edit', newId: newRequestId() }); };
  const held = deskReturn ? { state: 'none' as const, heldFor: null, ready: true } : heldForPick(holders.data?.holders, heldChoice);
  // The desk's own money in Flagged: null before db/0109 (the server cannot tell it apart yet), else its figure.
  const deskHeld = (holders.data as { desk?: { held: string } | null } | undefined)?.desk ?? null;
  const press = () => {
    if (!destination) return;
    const p = nextPayload(sheet, { toWalletId: destination.id, amount: (fixedAmount ?? amount).trim(), from, heldFor: from === 'frozen' ? held.heldFor : null });
    if (withDeal && !dealFor.current.has(p.requestId)) dealFor.current.set(p.requestId, deal);
    m.mutate(p);
  };
  const retryReads = () => { void limits.refetch(); if (wantsFlagged) { void approvals.refetch(); void balances.refetch(); void holders.refetch(); } };
  const blocked = newPaymentBlocked({ amount: shownAmount, windowOpen, attempt: attempt?.state ?? null, readiness: readiness.state, allowance, releaseOpen, heldForReady: shownFrom !== 'frozen' || held.ready });

  return {
    attempt, locked, pending: sending(sheet), uncertain: replayable(sheet), refused: refusedAttempt(sheet), queued: attempt?.state === 'queued',
    fixedAmount, shownAmount, shownFrom, problem, windowRead: win.data !== undefined, windowOpen, readiness, allowance, releaseOpen, blocked,
    setAmount: (v: string) => edit(() => setAmount(v)), setFrom: (v: PayoutSource) => edit(() => setFrom(v)),
    // Whose held money a payment out of Flagged is (components/HeldForField.tsx reads these).
    holders: holders.data?.holders, holdersFailed: holders.isError, retryHolders: () => void holders.refetch(), heldChoice,
    // The depositor the press would name, and the name to read back (null for every other source).
    heldFor: shownFrom === 'frozen' && !deskReturn ? (locked && attempt ? (attempt.heldFor ?? null) : held.heldFor) : null,
    deskReturn, deskHeld, deskHeldRead: holders.data !== undefined, deskHeldFailed: holders.isError,
    // The deal the press names (P-063): the one it was sent with once pressed, else the choice.
    withDeal, deal: locked && attempt ? dealFor.current.get(attempt.requestId) ?? null : deal, setDeal: (v: string | null) => edit(() => setDeal(v)),
    setHeldChoice: (v: string | null) => edit(() => setHeldChoice(v)),
    press, retryReads, readsPending: limits.isFetching || approvals.isFetching || balances.isFetching,
    readError: limits.error ?? approvals.error ?? balances.error,
    mutating: m.isPending, byKey, voiding, voidPress: () => { if (attempt) voiding.mutate(attempt.requestId); },
  };
}
export type MoveAttemptView = ReturnType<typeof useMoveAttempt>;
